import { styled } from "@mui/material/styles";
import { Popper, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";

const StyledDatePickerContainer = styled("div")`
  max-width: 100%;
  position: relative;
`;

const StyledDatePickerLabel = styled("span")`
  display: inline-block;
  width: max-content;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.palette.common.black};
  font-size: ${({ theme }) => theme.typography.bodyM?.fontSize};
  line-height: ${({ theme }) => theme.typography.bodyM?.lineHeight};
  margin-bottom: 5px;
  font-weight: 300;
  user-select: none;

  & > .date-picker__marker {
    display: inline-block;
    margin-left: 3px;
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;

  & .MuiInputBase-root {
    padding-right: 24px;
    width: 100%;
    min-height: 48px;
    background-color: ${({ theme }) => theme.palette.customGrey.soft};
    font-size: ${({ theme }) => theme.typography.bodyL?.fontSize};
    line-height: ${({ theme }) => theme.typography.bodyL?.lineHeight};
    color: ${({ theme }) => theme.palette.common.black};

    &:hover,
    &.Mui-focused {
      & .MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }) => theme.palette.primary.main};
        transition: all 0.2s linear;
        border-width: 1px;
      }
    }

    & > .MuiOutlinedInput-notchedOutline {
      border-width: 1px;
      border-color: transparent;
      transition: all 0.2s linear;
    }

    &.Mui-error {
      background-color: ${({ theme }) => theme.palette.red.light};

      & > .MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }) => theme.palette.red.main};
      }
    }

    &.Mui-disabled {
      color: ${({ theme }) => theme.palette.customGrey.main};
      -webkit-text-fill-color: ${({ theme }) => theme.palette.customGrey.main};
      background-color: ${({ theme }) => theme.palette.customGrey.soft};
      user-select: none;
    }

    &.Mui-disabled > .MuiOutlinedInput-notchedOutline {
      border-color: transparent;
    }
  }

  & .MuiInputBase-input {
    height: 100%;
    padding: 12px 24px;
    width: 100%;
    caret-color: ${({ theme }) => theme.palette.primary.main};

    &::placeholder {
      color: ${({ theme }) => theme.palette.customGrey.main};
      font-size: ${({ theme }) => theme.typography.bodyL?.fontSize};
      line-height: ${({ theme }) => theme.typography.bodyL?.lineHeight};
      opacity: 1;
      font-weight: 300;
      user-select: none;
    }

    &.Mui-disabled {
      color: ${({ theme }) => theme.palette.customGrey.main};
      -webkit-text-fill-color: ${({ theme }) => theme.palette.customGrey.main};
      background-color: ${({ theme }) => theme.palette.customGrey.soft};
      user-select: none;
    }
  }

  & .MuiIconButton-root {
    color: ${({ theme }) => theme.palette.customGrey.dark};
    transition: all 0.2s linear;

    &:hover {
      color: ${({ theme }) => theme.palette.primary.main};
      transition: all 0.2s linear;

      & svg[stroke] {
        stroke: ${({ theme }) => theme.palette.primary.main};
      }
    }

    & svg {
      width: 22px;
      transition: all 0.2s linear;
    }

    &.Mui-disabled {
      & svg[stroke] {
        stroke: ${({ theme }) => theme.palette.customGrey.main};
      }
    }
  }
`;

const StyledDatePickerError = styled(Typography)`
  display: inline-block;
  opacity: 0;
  visibility: hidden;
  color: ${({ theme }) => theme.palette.red.main};
  transition: all 0.2s linear;
  margin-top: 5px;

  &.date-picker-error_visible {
    opacity: 1;
    visibility: visible;
    transition: all 0.2s linear;
  }
`;

const StyledDatePickerPopper = styled(Popper)`
  z-index: 3000;
  & .MuiPaper-root {
    box-shadow: 6px 12px 24px 0px rgba(156, 168, 191, 0.24) !important;

    & .MuiDateCalendar-root {
      background-color: ${({ theme }) => theme.palette.common.white};
      border-radius: 12px;
      width: 340px;

      & .MuiPickersCalendarHeader-label {
        color: ${({ theme }) => theme.palette.common.black};
        font-size: ${({ theme }) => theme.typography.h3.fontSize};
        line-height: ${({ theme }) => theme.typography.h3.lineHeight};
        user-select: none;
        transition: all 0.2s linear;

        &:hover {
          color: ${({ theme }) => theme.palette.primary.main};
          transition: all 0.2s linear;
        }
      }
    }

    & .MuiYearCalendar-root,
    & .MuiMonthCalendar-root {
      margin: 0 auto;
    }

    & .MuiPickersArrowSwitcher-button {
      color: ${({ theme }) => theme.palette.common.black};
      transition: all 0.2s linear;

      &:hover {
        color: ${({ theme }) => theme.palette.primary.main};
        transition: all 0.2s linear;
      }
    }

    & .MuiPickersCalendarHeader-switchViewButton {
      display: none;
    }

    & .MuiPickersCalendarHeader-root {
      padding: 0 20px 0 28px;
    }

    & .MuiDayCalendar-header > .MuiDayCalendar-weekDayLabel {
      color: ${({ theme }) => theme.palette.common.black};
      font-size: ${({ theme }) => theme.typography.bodyL?.fontSize};
      line-height: ${({ theme }) => theme.typography.bodyL?.lineHeight};
      font-weight: 500;
      user-select: none;
      width: 40px;
      height: 40px;
    }

    & .MuiPickersDay-root {
      color: ${({ theme }) => theme.palette.common.black};
      font-size: ${({ theme }) => theme.typography.bodyM?.fontSize};
      line-height: ${({ theme }) => theme.typography.bodyM?.lineHeight};
      font-weight: 500;
      width: 40px;
      height: 40px;

      &:hover {
        background-color: ${({ theme }) => theme.palette.primary.light};
      }

      &.Mui-selected {
        background-color: ${({ theme }) => theme.palette.primary.main};
        color: ${({ theme }) => theme.palette.common.white};
      }

      &.MuiPickersDay-today {
        border-color: ${({ theme }) => theme.palette.primary.main};
      }

      &.Mui-disabled {
        color: ${({ theme }) => theme.palette.customGrey.main};
      }
    }

    & .MuiPickersYear-root .MuiPickersYear-yearButton,
    & .MuiPickersMonth-root .MuiPickersMonth-monthButton {
      color: ${({ theme }) => theme.palette.common.black};
      font-size: ${({ theme }) => theme.typography.bodyL?.fontSize};
      line-height: ${({ theme }) => theme.typography.bodyL?.lineHeight};
      font-weight: 500;

      &:hover {
        background-color: ${({ theme }) => theme.palette.primary.light};
      }

      &.Mui-selected {
        background-color: ${({ theme }) => theme.palette.primary.main};
        color: ${({ theme }) => theme.palette.common.white};
      }
    }
  }
`;

export {
  StyledDatePickerContainer,
  StyledDatePickerLabel,
  StyledDatePicker,
  StyledDatePickerError,
  StyledDatePickerPopper,
};
